import type { DragEvent } from "react";
import type { UiManagerMainRoute } from "@/ui/runtime/contracts/manager-ui-v2";
import { Badge } from "@/ui/foundation/primitives/badge";
import { cn } from "@/ui/foundation/lib/utils";

export const routeTitle: Record<UiManagerMainRoute, string> = {
    "/manager/home": "Home",
    "/manager/competitions": "Standings",
    "/manager/inbox": "Inbox",
    "/manager/squad": "Squad",
    "/manager/fixtures": "Fixtures",
    "/manager/staff": "Club",
    "/manager/transfers": "Transfers",
};

export const clubSections = [
    { id: "staff", label: "Staff", description: "Hiring, contracts, structure" },
    { id: "ground", label: "Ground", description: "Capacity, condition, upgrades" },
    { id: "finances", label: "Finances", description: "Budget, wages, cashflow" },
    { id: "identity", label: "Identity", description: "Culture and squad profile" },
] as const;

export const helpArticles = [
    {
        id: "getting-started",
        title: "Getting Started",
        excerpt: "How to run your first day and understand the dashboard.",
        body: "Start on Home, review inbox actions, then advance with the top-right action button. Use Squad to monitor readiness and Transfers to target recruits.",
    },
    {
        id: "inbox-actions",
        title: "Inbox Decisions",
        excerpt: "How inbox choices affect squad morale and events.",
        body: "Unread inbox items can carry decision options. Choosing an option applies effects immediately and can influence morale, selection pressure, and board confidence.",
    },
    {
        id: "matchday-flow",
        title: "Matchday Flow",
        excerpt: "What happens when the action changes to Play Match.",
        body: "When a fixture is due, the action label changes to Play Match. Open match intro, confirm lineup, then start or skip the match to return with a result summary.",
    },
    {
        id: "transfers-basics",
        title: "Transfers Basics",
        excerpt: "Scout and express interest from Transfers.",
        body: "Use Transfers to compare targets, scout, and express interest. Bids, wage offers, and walk-away choices are handled through actionable inbox messages.",
    },
] as const;

export const RAIL_ICON_CLASS = "size-6";
export const RAIL_ICON_STROKE = 1.4;
export const TEAM_SELECTION_CARD_WIDTH_CLASS = "w-40";
export const TEAM_SELECTION_LIST_CARD_CLASS = `h-14 ${TEAM_SELECTION_CARD_WIDTH_CLASS} min-w-0 flex-none rounded-md border px-2 py-1.5 text-left`;
export const TEAM_SELECTION_PITCH_CARD_CLASS = `h-14 ${TEAM_SELECTION_CARD_WIDTH_CLASS} min-w-0 flex-none rounded-md border bg-background/90 px-2 py-1 text-left shadow`;

export interface FixtureMonth {
    id: string;
    label: string;
    matches: Array<{
        id: string;
        dateIso: string;
        dateLabel: string;
        home: string;
        away: string;
        competition: string;
        result: string;
        isManagedFixture: boolean;
    }>;
}

export const fallbackFixtureMonth: FixtureMonth = {
    id: "none",
    label: "No fixtures",
    matches: [],
};

export function renderPips(value: number) {
    const normalized = Math.max(0, Math.min(100, value));
    const active = Math.round((normalized / 100) * 5);
    return (
        <div className="flex items-center gap-1">
            {Array.from({ length: 5 }, (_, index) => (
                <span
                    key={`pip-${index}`}
                    className={cn(
                        "inline-block h-2 w-2 rounded-full border",
                        index < active ? "bg-primary border-primary" : "bg-muted border-border"
                    )}
                />
            ))}
            <span className="ml-1 text-xs text-muted-foreground">{value}</span>
        </div>
    );
}

export function normalizeRating(value: number | null | undefined): number {
    const numeric = typeof value === "number" && Number.isFinite(value) ? value : 50;
    if (numeric <= 0) {
        return 50;
    }
    if (numeric <= 1) {
        return Math.max(0, Math.min(100, numeric * 100));
    }
    if (numeric <= 5) {
        return Math.max(0, Math.min(100, numeric * 20));
    }
    return Math.max(0, Math.min(100, numeric));
}

function getRatingPipTone(normalizedRating: number): "low" | "mid-low" | "mid" | "high" {
    if (normalizedRating < 40) {
        return "low";
    }
    if (normalizedRating < 60) {
        return "mid-low";
    }
    if (normalizedRating < 75) {
        return "mid";
    }
    return "high";
}

export function renderCompactPips(value: number | null | undefined) {
    const normalized = normalizeRating(value);
    const pipScore = Math.round((normalized / 10)) / 2;
    const tone = getRatingPipTone(normalized);
    return (
        <div className="ml-2 flex shrink-0 items-center gap-1">
            {Array.from({ length: 5 }, (_, index) => (
                (() => {
                    const fillAmount = Math.max(0, Math.min(1, pipScore - index));
                    const quantizedFill = fillAmount >= 0.75 ? 1 : fillAmount >= 0.25 ? 0.5 : 0;
                    return (
                        <span
                            key={`compact-pip-${index}`}
                            className={cn(
                                "compact-rating-pip inline-block size-2 rounded-full border",
                                `compact-rating-pip--${tone}`,
                                quantizedFill === 1 ? "compact-rating-pip--full" : quantizedFill === 0.5 ? "compact-rating-pip--half" : "compact-rating-pip--empty"
                            )}
                        />
                    );
                })()
            ))}
        </div>
    );
}

export function FormResultPill({ code }: { code: string }) {
    return (
        <Badge className={cn("h-5 w-5 justify-center rounded-sm px-0 py-0 text-[10px] font-semibold", formBadgeClass(code))}>
            {code === "-" ? "" : code}
        </Badge>
    );
}

export function initials(name: string): string {
    return name.split(" ").map((part) => part.charAt(0)).join("").slice(0, 2).toUpperCase();
}

export function shortSelectionName(name: string): string {
    const parts = String(name ?? "").trim().split(/\s+/).filter(Boolean);
    if (parts.length <= 1) {
        return parts[0] ?? "";
    }
    const firstInitial = parts[0]?.charAt(0).toUpperCase() ?? "";
    const rest = parts.slice(1).join(" ");
    return `${firstInitial}. ${rest}`;
}

export function avatarDataUri(svg?: string): string | undefined {
    if (!svg) {
        return undefined;
    }
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

export function setCardDragPreview(event: DragEvent<HTMLElement>): void {
    const card = event.currentTarget;
    const rect = card.getBoundingClientRect();
    const preview = card.cloneNode(true) as HTMLElement;
    preview.style.position = "fixed";
    preview.style.left = "-9999px";
    preview.style.top = "0";
    preview.style.width = `${Math.round(rect.width)}px`;
    preview.style.height = `${Math.round(rect.height)}px`;
    preview.style.pointerEvents = "none";
    preview.style.zIndex = "9999";
    preview.style.opacity = "1";
    preview.style.transform = "none";
    preview.querySelectorAll("img").forEach((image) => {
        image.draggable = false;
    });
    document.body.appendChild(preview);
    event.dataTransfer.setDragImage(preview, Math.min(rect.width / 2, 120), rect.height / 2);
    requestAnimationFrame(() => {
        preview.remove();
    });
}

export function formatDateLabel(value: string): string {
    const safe = value.length <= 10 ? `${value}T12:00:00.000Z` : value;
    const parsed = new Date(safe);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }
    return new Intl.DateTimeFormat(undefined, { month: "short", day: "2-digit", year: "numeric" }).format(parsed);
}

export function formatFriendlyDate(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) {
        return "--";
    }
    const lower = trimmed.toLowerCase();
    if (lower === "today" || lower === "yesterday" || lower === "tomorrow") {
        return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
    }
    return formatDateLabel(trimmed);
}

export function formatMoney(value: number): string {
    return `£${Math.round(value).toLocaleString()}`;
}

export function formatTransferMarketValue(value: number): string {
    const millions = Math.max(0, value) / 1000000;
    return `£${millions.toFixed(1)}m`;
}

export function transferStatusLabel(status: string): string {
    const labels: Record<string, string> = {
        scouted: "Scouted",
        scouting_in_progress: "Scouting",
        cooldown: "Cooling Off",
        shortlisted: "Shortlisted",
        bid_prepared: "Bid Prepared",
        awaiting_club_response: "Awaiting Club",
        club_rejected: "Rejected",
        club_accepted: "Club Accepted",
        awaiting_player_response: "Awaiting Player",
        personal_terms_failed: "Terms Failed",
        deal_agreed: "Deal Agreed",
        signed: "Signed",
        offer_received: "Offer Received",
        offer_countered: "Countered",
        withdrawn: "Withdrawn",
        expired: "Expired"
    };
    return labels[status] ?? "Scouted";
}

export function transferStatusVariant(status: string): "default" | "secondary" | "outline" {
    if (status === "deal_agreed" || status === "club_accepted" || status === "awaiting_player_response") {
        return "default";
    }
    if (status === "scouting_in_progress" || status === "cooldown" || status === "bid_prepared" || status === "awaiting_club_response" || status === "offer_countered") {
        return "secondary";
    }
    return "outline";
}

export function transferNextStep(status: string): string {
    if (status === "scouting_in_progress") {
        return "Scouting in progress. Advance days to improve confidence and reduce uncertainty.";
    }
    if (status === "cooldown") {
        return "You cancelled an agreed deal. This club is refusing talks for a short period.";
    }
    if (status === "scouted" || status === "shortlisted") {
        return "Prepare a bid draft when you're ready to open negotiations.";
    }
    if (status === "bid_prepared") {
        return "Submit terms to send this offer to the selling club.";
    }
    if (status === "offer_countered") {
        return "Club countered. Update fee and submit terms again or move on.";
    }
    if (status === "deal_agreed") {
        return "Deal agreed. Finalize to complete the signing.";
    }
    if (status === "signed") {
        return "Transfer complete.";
    }
    return "Monitor negotiation updates and react as needed.";
}

export function normalizeForm(form: string[] | undefined): string[] {
    const recent = (form ?? []).slice(-5);
    while (recent.length < 5) {
        recent.push("-");
    }
    return recent;
}

export function formBadgeClass(code: string): string {
    if (code === "W") {
        return "bg-emerald-600 text-white";
    }
    if (code === "L") {
        return "bg-red-600 text-white";
    }
    if (code === "D") {
        return "bg-zinc-500 text-white";
    }
    return "bg-zinc-200 text-zinc-500";
}

export function attributeBarTone(score: number): string {
    if (score < 35) {
        return "bg-red-500";
    }
    if (score < 55) {
        return "bg-orange-500";
    }
    if (score < 75) {
        return "bg-yellow-500";
    }
    return "bg-green-500";
}

export function roleLane(role: string): "gk" | "def" | "mid" | "att" | "other" {
    const normalized = role.toUpperCase();
    if (normalized === "GK") {
        return "gk";
    }
    if (normalized.includes("CB") || normalized.includes("LB") || normalized.includes("RB") || normalized.includes("WB")) {
        return "def";
    }
    if (normalized.includes("CM") || normalized.includes("DM") || normalized.includes("AM") || normalized.includes("LM") || normalized.includes("RM")) {
        return "mid";
    }
    if (normalized.includes("ST") || normalized.includes("CF") || normalized.includes("RW") || normalized.includes("LW")) {
        return "att";
    }
    return "other";
}
